import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import { fetchPost } from '../api/common';
import CheckIcon from '@mui/icons-material/Check';
import { DetailedUserAchievement } from '@par/shared/types/player';

type PropTypes = {
  detailedUserAchievements?: DetailedUserAchievement[];
  achievementsShowcase: string[];
  close: () => void;
  onUpdateProfile: () => void;
  open: boolean;
  profileId: string;
};

export const AchievementsShowcaseDialog = ({
  detailedUserAchievements,
  achievementsShowcase,
  close,
  onUpdateProfile,
  open,
  profileId,
}: PropTypes) => {
  const theme = useTheme();
  const [checked, setChecked] = useState<string[]>(achievementsShowcase);

  const MAX_SELECTIONS = 3;

  const handleToggle = (achievement: string) => () => {
    if (checked.length === MAX_SELECTIONS && !checked.includes(achievement))
      return;
    let newChecked;
    if (checked.includes(achievement)) {
      newChecked = checked.filter((c) => c !== achievement);
    } else {
      newChecked = checked.concat(achievement);
    }
    setChecked(newChecked);
  };

  const handleSaveBan = async () => {
    await fetchPost(`/profile/achievements-showcase`, {
      profileId,
      achievements: checked,
    });
    onUpdateProfile();
    close();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6" textAlign={'center'} sx={{ letterSpacing: 5 }}>
          Showcase your achievements!
        </Typography>
        <Stack mt={2} direction="row" spacing={2} justifyContent={'center'}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ color: theme.palette.success.main }}
          >
            {MAX_SELECTIONS - checked.length}
          </Typography>
          <Typography variant="subtitle1">
            selection{checked.length !== 1 ? '' : 'S'} left
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <List
          sx={{
            borderRadius: 2,
            width: '100%',
            bgcolor: 'background.paper',
          }}
        >
          {detailedUserAchievements.map((achievement) => {
            const labelId = `checkbox-list-label-${achievement.achievement.name}`;

            return (
              <ListItem key={achievement.achievement.name} disablePadding>
                <ListItemButton
                  role={undefined}
                  onClick={handleToggle(achievement.achievement.name)}
                  dense
                  sx={{ paddingTop: 0, paddingBottom: 0 }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={checked.includes(achievement.achievement.name)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-labelledby': labelId }}
                      checkedIcon={
                        <CheckIcon sx={{ color: theme.palette.success.main }} />
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    id={labelId}
                    primary={achievement.achievement.displayName}
                    primaryTypographyProps={{
                      fontWeight: 'medium',
                      variant: 'body2',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={close} fullWidth>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSaveBan} fullWidth>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
